import { AfterViewInit, Component, ElementRef, Inject, ViewChild, inject } from '@angular/core';
import { Store } from '@ngrx/store';

import { MAT_DIALOG_DATA, MatDialog, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import { UnitsService } from '../../shared/services/units.service';
import { Xl200Service } from '../../shared/services/xl200.service';
import { IFlatView, PatternsApiService } from '../patterns-api.service';
import { outlineExtentsIn, shapeOutlinePath } from '../../shared/services/shape-outline';
import { EditRoles, UserHasRoles } from '../../shared/services/store/user/selector';

/**
 * Flat-view visualization (specs/pattern-feature-reference.md): a picture of the flat blank as
 * the solver receives it, drawn from the flatview endpoint — which runs the real solve path and
 * stops at the FindSolution boundary, so the picture cannot drift from the computation. The
 * frame is post-flip ("as the machine runs it"); per-hole tooltips carry the pre-flip segment
 * binding chain (vendor name → canonical segment → anchor → resolved Y).
 *
 * Rendering is uniform (true) scale: parts are long and narrow, so the strip width fits the
 * modal height and the length scrolls horizontally rather than distorting aspect.
 */

const PAD = 48; // px margin around the strip inside the SVG

interface RGuide {
  y: number;
  labelY: number;
  x1: number;
  x2: number;
  label: string;
  cls: string;
  tooltip: string;
}

interface RHole {
  cx: number;
  cy: number;
  /** Resolved coordinates in inches (drawing frame), kept for dimension callouts. */
  xIn: number;
  yIn: number;
  /** Bound through a flange segment — hover adds the bend-line → hole dimension. */
  flangeRef: boolean;
  /** Half-extents of the drawn outline in px, for breaking reference lines around the punch. */
  halfW: number;
  halfH: number;
  /** True outline from the shape geometry library, else null → nominal circle marker. */
  path: string | null;
  /** Radius of the nominal marker (only drawn when path is null). */
  r: number;
  /** Half-length of the center-mark plus arms, in px. */
  arm: number;
  trueSize: boolean;
  tooltip: string;
}

/** One ordinate X callout in show-all mode: holes sharing a displayed X collapse to one. */
interface RDimX {
  x: number;
  /** Leader from the callout down through every instance at this X, broken at each punch. */
  ext: string;
  labelX: number;
  labelY: number;
  label: string;
}

/** One ordinate Y callout, pinned to the visible right edge. */
interface RDimY {
  /** True y of the row (tick position). */
  y: number;
  /** De-collided label y. */
  labelY: number;
  label: string;
  /** Full-width reference line through every instance at this Y, broken at each punch. */
  ext: string;
}

/** A single measured dimension line (hover): extension lines + arrowed line + label. */
interface RDimLine {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  ext: string;
  label: string;
  labelX: number;
  labelY: number;
  anchor: 'start' | 'middle' | 'end';
}

const KIND_LABELS: { [k: string]: string } = {
  Round: 'Round',
  RoundedSlot: 'Rounded slot',
  Square: 'Square',
  SquareSlot: 'Square slot',
  Triangle: 'Triangle',
};

const GUIDE_DEFS: { [key: string]: { label: string; cls: string } } = {
  stripCenter: { label: 'Strip center', cls: 'guide--center' },
  // Only emitted when the tooling code has a nonzero strip offset: the strip is physically
  // shifted in the machine, so machine Y 0 is not the strip center in this drawing's frame.
  machineCenter: { label: 'Machine center', cls: 'guide--machine-center' },
  stripEdgePlus: { label: 'Strip edge +', cls: 'guide--edge' },
  stripEdgeMinus: { label: 'Strip edge −', cls: 'guide--edge' },
  webCenter: { label: 'Web center (Δ)', cls: 'guide--web-center' },
  webFlatPlus: { label: 'Web flat +', cls: 'guide--web-flat' },
  webFlatMinus: { label: 'Web flat −', cls: 'guide--web-flat' },
  bendPlus: { label: 'Bend +', cls: 'guide--bend' },
  bendMinus: { label: 'Bend −', cls: 'guide--bend' },
  flangeTipPlus: { label: 'Flange tip +', cls: 'guide--tip' },
  flangeTipMinus: { label: 'Flange tip −', cls: 'guide--tip' },
};

@Component({
  selector: 'app-flat-view-visualizer',
  templateUrl: './flat-view-visualizer.component.html',
  styleUrls: ['./flat-view-visualizer.component.scss'],
  imports: [
    MatDialogModule,
    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    TranslateModule
],
})
export class FlatViewVisualizerComponent implements AfterViewInit {
  @ViewChild('scroller') scrollerRef!: ElementRef<HTMLDivElement>;

  view: IFlatView | null = null;
  loading = true;
  loadError: string | null = null;

  // Pixels per inch — uniform for X and Y (true scale).
  scale = 4;

  svgW = 800;
  svgH = 480;
  midY = 240;
  strip = { x: 0, y: 0, w: 0, h: 0 };
  widthKnown = false;
  guides: RGuide[] = [];
  holes: RHole[] = [];
  leadingEdgeX = PAD;
  trailingEdgeX = PAD;
  /** A macro viewed on its own: the X of its one call, which its rows are placed from. */
  callLine: { x: number; tooltip: string } | null = null;
  lengthLabel = '';
  stripTooltip = '';
  // Guide labels are pinned to the visible left edge while the length scrolls.
  labelShiftX = 0;
  // Ordinate Y callouts are pinned to the visible right edge the same way.
  rightShiftX = 0;

  // Dimensions. Hover always measures the hole under the cursor against the datums; the
  // show-all toggle draws ordinate callouts instead of per-hole dimension lines, so dense
  // patterns collapse to one callout per unique coordinate value rather than a lattice of
  // crossing dimension lines. The toggle persists across dialogs per browser.
  showDims = localStorage.getItem('flatView.showDims') === 'true';
  dimXs: RDimX[] = [];
  dimYs: RDimY[] = [];
  hoverDims: RDimLine[] = [];
  // Hovered-hole details, shown in a corner card instead of a cursor tooltip so they never
  // sit on top of the dimension graphics. The card flips to whichever side the hole isn't on.
  hoverInfo: string[] = [];
  hoverInfoLeft = false;
  // Web-flat (bend line) positions from the solve's guides, for flange-referenced dims.
  private webFlatPlusIn: number | null = null;
  private webFlatMinusIn: number | null = null;

  // View orientation — drawn as a plan view with the operator at the bottom, so the picture
  // matches the physical flat on this machine's runout. View-only; the resolved coordinates
  // are untouched. Stored per machine on the server (MachineExt.Orientation), shared with the
  // machine layout visualizer and every user.
  /** Part travels left→right (leading edge exits at page-right). False = right-to-left. */
  flowRight = true;
  /** Machine + side is away from the operator (drawn up). False = toward the operator. */
  plusFar = true;

  /**
   * The toggles save the machine's shared orientation as you click them, and the picture is meant
   * to match the machine's physical runout, so a flip is only offered to users who may change
   * machine configuration. Everyone else sees the stored orientation.
   */
  readonly canEditOrientation = inject(Store).selectSignal(UserHasRoles(EditRoles.machines, false));

  constructor(
    public dialogRef: MatDialogRef<FlatViewVisualizerComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      patternId: string;
      patternName: string;
      machineNumber: number;
      toolingCode: string | null;
      gauge?: number | null;
      lengthIn: number;
      widthIn: number;
    },
    private patternsApi: PatternsApiService,
    private unitsService: UnitsService,
    private xl200Service: Xl200Service,
    private dialog: MatDialog
  ) {}

  /**
   * The 3D formed view, fed with this dialog's already-loaded flat view and orientation.
   * Dynamically imported so the Babylon engine stays out of the main bundle — it only
   * downloads the first time someone opens the 3D view.
   */
  async open3d(): Promise<void> {
    if (!this.view) {
      return;
    }
    const { Part3dVisualizerComponent } = await import(
      '../part-3d-visualizer/part-3d-visualizer.component'
    );
    this.dialog.open(Part3dVisualizerComponent, {
      data: { view: this.view, flowRight: this.flowRight, plusFar: this.plusFar },
      width: '92vw',
      maxWidth: '92vw',
    });
  }

  setFlow(flowRight: boolean): void {
    if (!this.canEditOrientation()) {
      return;
    }
    this.flowRight = flowRight;
    this.saveOrientation();
    this.render();
  }

  setPlusFar(plusFar: boolean): void {
    if (!this.canEditOrientation()) {
      return;
    }
    this.plusFar = plusFar;
    this.saveOrientation();
    this.render();
  }

  private saveOrientation(): void {
    this.xl200Service
      .saveMachineOrientation(this.data.machineNumber, {
        flowRight: this.flowRight,
        plusSideFar: this.plusFar,
      })
      .subscribe({ error: () => {} });
  }

  ngAfterViewInit(): void {
    this.xl200Service.getMachineOrientation(this.data.machineNumber).subscribe({
      next: (o) => {
        this.flowRight = o.flowRight;
        this.plusFar = o.plusSideFar;
        if (this.view) {
          this.render();
        }
      },
      error: () => {
        // Orientation unavailable — the defaults apply.
      },
    });

    this.patternsApi
      .getFlatView(
        this.data.patternId,
        this.data.machineNumber,
        this.data.toolingCode,
        this.data.lengthIn,
        this.data.widthIn,
        this.data.gauge ?? null
      )
      .subscribe({
        next: (view) => {
          this.view = view;
          this.loading = false;
          // Defer until the dialog has laid out so the scroller has a real size.
          setTimeout(() => this.fit());
        },
        error: (err) => {
          this.loading = false;
          this.loadError = err?.error?.errors?.join(' ') ?? 'Could not load the flat view.';
        },
      });
  }

  // Extra headroom above the strip when ordinate X callouts are shown (rotated labels).
  get pad(): number {
    return this.showDims ? PAD + 40 : PAD;
  }

  toggleDims(): void {
    this.showDims = !this.showDims;
    localStorage.setItem('flatView.showDims', String(this.showDims));
    this.render();
    // The pad change resizes the SVG; re-pin after layout settles.
    setTimeout(() => this.updatePins());
  }

  get flipAnnotation(): string {
    if (!this.view) {
      return '';
    }
    const parts: string[] = [];
    if (this.view.appliedFlipX === null || this.view.appliedFlipY === null) {
      return 'Mixed per-shape flips — see hole tooltips';
    }
    if (this.view.appliedFlipX) {
      parts.push('Flip X');
    }
    if (this.view.appliedFlipY) {
      parts.push('Flip Y');
    }
    return parts.length ? `${parts.join(' + ')} applied` : 'No flips';
  }

  /** What the view shows when the pattern is a macro: one call to it, at the middle. */
  get macroAnnotation(): string {
    const kind = this.view?.macroInstance?.kind;
    switch (kind) {
      case 'Relocatable':
        return 'Relocatable shape macro — one call at the middle, on the strip center';
      case 'Placed':
        return 'Placed shape macro — one call at the middle; its rows supply Y';
      case 'Tool':
        return 'Tool macro — no shapes to draw';
      case 'Invalid':
        return 'Invalid shape macro';
      default:
        return '';
    }
  }

  /** The ignored shape codes and shape macros, with how many holes each dropped. */
  get ignoredSummary(): string {
    const v = this.view;
    return [
      ...(v?.ignoredShapes ?? []).map((s) => `${s.shapeCode} ×${s.count}`),
      ...(v?.ignoredMacros ?? []).map((m) => `macro ${m.macroName} ×${m.count}`),
    ].join(', ');
  }

  // All lengths in the dialog honor the user's configured format — decimal inches,
  // feet + fractional inches (ffi), feet + decimal inches (fdi), or metric.
  fmt(v: number): string {
    return this.unitsService.formatUserUnits(v, 'in', 3);
  }

  // The applied per-bend corrections, one per side (the v2 (gauge, side, direction) cells);
  // collapsed to a single value when both sides match.
  corrAnnotation(g: IFlatView['geometry']): string {
    if (!g || (!g.bendCorrectionPlus && !g.bendCorrectionMinus)) {
      return '';
    }
    if (g.bendCorrectionPlus === g.bendCorrectionMinus) {
      return ` (corr ${this.fmt(g.bendCorrectionPlus)})`;
    }
    return ` (corr + ${this.fmt(g.bendCorrectionPlus)} / − ${this.fmt(g.bendCorrectionMinus)})`;
  }

  fit(): void {
    if (!this.view) {
      return;
    }
    const el = this.scrollerRef?.nativeElement;
    const availW = Math.max((el?.clientWidth ?? 900) - 8, 300);
    const availH = Math.max((el?.clientHeight ?? 520) - 8, 220);
    // Whole part in view, uniform scale: for these long, narrow parts the length (X) is the
    // binding constraint; the width bound only matters for short wide blanks.
    const fitX = (availW - 2 * this.pad) / this.view.lengthIn;
    const fitY = (availH - 2 * this.pad) / this.stripHeightIn();
    this.scale = Math.max(Math.min(fitX, fitY), 0.05);
    this.render();
    setTimeout(() => this.updatePins());
  }

  zoomIn(): void {
    this.zoomAboutViewportCenter(1.25);
  }

  zoomOut(): void {
    this.zoomAboutViewportCenter(0.8);
  }

  onScroll(): void {
    this.updatePins();
  }

  private updatePins(): void {
    const el = this.scrollerRef?.nativeElement;
    this.labelShiftX = el?.scrollLeft ?? 0;
    this.rightShiftX = el ? Math.min(this.svgW, el.scrollLeft + el.clientWidth) : this.svgW;
  }

  onWheel(e: WheelEvent): void {
    e.preventDefault();
    const el = this.scrollerRef.nativeElement;
    const rect = el.getBoundingClientRect();
    this.zoomAbout(e.deltaY < 0 ? 1.1 : 1 / 1.1, e.clientX - rect.left, e.clientY - rect.top);
  }

  private zoomAboutViewportCenter(factor: number): void {
    const el = this.scrollerRef?.nativeElement;
    this.zoomAbout(factor, (el?.clientWidth ?? 0) / 2, (el?.clientHeight ?? 0) / 2);
  }

  // Zoom is uniform (the view is true scale); the anchor is a point in the scroller's
  // viewport, kept stationary by re-scrolling after the SVG resizes.
  private zoomAbout(factor: number, viewportX: number, viewportY: number): void {
    const el = this.scrollerRef?.nativeElement;
    if (!el || !this.view) {
      return;
    }

    const px = viewportX + el.scrollLeft;
    const py = viewportY + el.scrollTop;
    const xIn = (px - this.pad) / this.scale;
    const yIn = (this.midY - py) / this.scale;

    this.scale *= factor;
    this.render();

    // Defer until Angular has applied the new SVG size, else the scroll gets clamped
    // against the old content extents.
    setTimeout(() => {
      el.scrollLeft += this.pad + xIn * this.scale - px;
      el.scrollTop += this.midY - yIn * this.scale - py;
      this.updatePins();
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  // The drawn strip height: the material width when known, otherwise enough to contain
  // every guide and hole (widthless materials are legal — the solve substitutes a dummy).
  private stripHeightIn(): number {
    if (!this.view) {
      return 10;
    }
    if (this.view.widthIn) {
      return this.view.widthIn;
    }
    const ys = [
      1,
      ...this.view.guides.map((g) => Math.abs(g.y)),
      ...this.view.holes.map((h) => Math.abs(h.y)),
    ];
    return Math.max(...ys) * 2 + 2;
  }

  private render(): void {
    const v = this.view;
    if (!v) {
      return;
    }

    const s = this.scale;
    const pad = this.pad;
    const stripH = this.stripHeightIn();
    this.widthKnown = v.widthIn != null;
    this.svgW = v.lengthIn * s + 2 * pad;
    this.svgH = stripH * s + 2 * pad;
    this.midY = this.svgH / 2;
    this.hoverDims = [];
    this.hoverInfo = [];
    this.webFlatPlusIn = v.guides.find((g) => g.key === 'webFlatPlus')?.y ?? null;
    this.webFlatMinusIn = v.guides.find((g) => g.key === 'webFlatMinus')?.y ?? null;

    // Plan view with the operator at the bottom: the leading edge sits at the exit end
    // (right for a left-to-right machine), and machine + is drawn up when it's the far side.
    const x = (xIn: number) => pad + (this.flowRight ? v.lengthIn - xIn : xIn) * s;
    const y = (yIn: number) => (this.plusFar ? this.midY - yIn * s : this.midY + yIn * s);

    this.strip = {
      x: pad,
      y: this.midY - (stripH / 2) * s,
      w: v.lengthIn * s,
      h: stripH * s,
    };
    this.leadingEdgeX = x(0);
    this.trailingEdgeX = x(v.lengthIn);
    this.callLine = v.macroInstance
      ? {
          x: x(v.macroInstance.callX),
          tooltip:
            v.macroInstance.kind === 'Placed'
              ? `Macro call: Leading Center 0, X ${this.fmt(v.macroInstance.callX)}. ` +
                'Each row sits its X offset from here; its segment reference gives Y.'
              : `Macro call: Leading Center 0, X ${this.fmt(v.macroInstance.callX)}, Y Default ` +
                '(the strip center). Each row sits its X and Macro± offsets from here.',
        }
      : null;
    this.lengthLabel = this.fmt(v.lengthIn);
    this.stripTooltip =
      v.widthIn != null
        ? `Strip ${this.fmt(v.lengthIn)} × ${this.fmt(v.widthIn)}`
        : 'Material has no width — showing resolved extents';

    this.guides = v.guides
      .filter((g) => {
        // With zero bend allowance the bend lines coincide with the web flat boundaries —
        // drawing both just doubles the ink.
        if ((g.key === 'bendPlus' || g.key === 'bendMinus') && !v.geometry?.bendAllowance) {
          return false;
        }
        return true;
      })
      .map((g) => {
        const def = GUIDE_DEFS[g.key] ?? { label: g.key, cls: 'guide--other' };
        const gy = y(g.y);
        return {
          y: gy,
          labelY: gy - 5,
          x1: pad,
          x2: pad + v.lengthIn * s,
          label: def.label,
          cls: def.cls,
          tooltip: `${def.label}: Y ${this.fmt(g.y)}`,
        };
      });

    // De-collide labels of nearly-coincident lines (e.g. strip edge vs flange tip): walk them
    // top-down and push any label that would overlap the previous one below it.
    const byY = [...this.guides].sort((a, b) => a.y - b.y);
    let lastLabelY = -Infinity;
    for (const g of byY) {
      if (g.labelY - lastLabelY < 15) {
        g.labelY = Math.max(g.y + 15, lastLabelY + 15);
      }
      lastLabelY = g.labelY;
    }

    this.holes = v.holes.map((h) => {
      const lines: string[] = [];
      lines.push(`${h.shapeCode ?? '?'}${h.toolId != null ? ` (tool ${h.toolId})` : ''}`);
      if (h.originalShapeCode) {
        lines.push(`alias of ${h.originalShapeCode}`);
      }
      if (h.macro) {
        lines.push(`From macro ${h.macro.name}, row ${h.macro.row}`);
      }
      lines.push(`X: ${this.fmt(h.x)}   Y: ${this.fmt(h.y)}`);
      const flips = [h.flipX ? 'Flip X' : null, h.flipY ? 'Flip Y' : null].filter(Boolean);
      if (flips.length) {
        lines.push(`${flips.join(' + ')} applied`);
      }
      if (h.segment) {
        // Pre-flip binding chain: how the segment reference became a Y coordinate.
        lines.push(`Segment: ${h.segment.name} → ${h.segment.canonicalName}`);
        lines.push(`Anchor: ${h.segment.anchor}, offset ${this.fmt(h.segment.yOffset)}`);
        if (h.segment.preFlipY != null) {
          lines.push(`Resolved Y (pre-flip): ${this.fmt(h.segment.preFlipY)}`);
        }
      }
      // True outline from the shape geometry library (the shape table's R is the solver's
      // match tolerance, never a size). No entry → the fixed nominal marker.
      const geo = h.geometry;
      const ext = geo != null ? outlineExtentsIn(geo) : null;
      let path: string | null = null;
      let extentPx = 0;
      let halfW = 4;
      let halfH = 4;
      if (geo != null && ext != null && ext.x > 0 && ext.y > 0) {
        const dims =
          geo.kind === 'Round' || geo.kind === 'Square'
            ? this.fmt(geo.xIn)
            : `${this.fmt(geo.xIn)} × ${this.fmt(geo.yIn)}`;
        lines.push(`${KIND_LABELS[geo.kind] ?? geo.kind} ${dims}`);
        // Keep true scale, but never let a hole vanish: below ~3px the outline is enlarged
        // uniformly (the tooltip carries the real dimensions).
        extentPx = Math.max(ext.x, ext.y) * s;
        const holeScale = extentPx < 3 ? (3 / Math.max(ext.x, ext.y)) : s;
        halfW = (ext.x * holeScale) / 2;
        halfH = (ext.y * holeScale) / 2;
        // The entry's direction is as-mounted; the per-hole shape-option FlipY applies on
        // top (XOR), then the view's own y inversion maps it to screen "up".
        const apexPositive = (geo.direction === 'Positive') !== h.flipY;
        const apexUp = this.plusFar ? apexPositive : !apexPositive;
        path = shapeOutlinePath(geo, x(h.x), y(h.y), holeScale, apexUp);
      }
      const nominalR = 4;
      const arm = path
        ? Math.min(Math.max((Math.max(extentPx, 3) / 2) * 0.6, 3), 9)
        : Math.min(Math.max(nominalR * 0.6, 3), 9);
      return {
        cx: x(h.x),
        cy: y(h.y),
        xIn: h.x,
        yIn: h.y,
        flangeRef: /flange/i.test(h.segment?.canonicalName ?? ''),
        halfW,
        halfH,
        path,
        r: nominalR,
        arm,
        trueSize: path != null,
        tooltip: lines.join('\n'),
      };
    });

    this.buildOrdinates();
  }

  // Show-all mode: ordinate callouts. Holes sharing a displayed X collapse into one rotated
  // label above the strip; holes sharing a displayed Y collapse into one label pinned to the
  // visible right edge. This is how punch drawings stay readable — a dense pattern shows one
  // callout per unique coordinate value, never a lattice of per-hole dimension lines.
  private buildOrdinates(): void {
    const xGroups = new Map<string, { x: number; bottom: number }>();
    const yGroups = new Map<string, { y: number; left: number }>();
    for (const h of this.holes) {
      const xLabel = this.fmt(h.xIn);
      const xg = xGroups.get(xLabel);
      const bottom = h.cy + h.halfH + 5;
      if (!xg) {
        xGroups.set(xLabel, { x: h.cx, bottom });
      } else {
        xg.bottom = Math.max(xg.bottom, bottom);
      }
      const yLabel = this.fmt(h.yIn);
      const yg = yGroups.get(yLabel);
      if (!yg) {
        yGroups.set(yLabel, { y: h.cy, left: h.cx });
      } else {
        yg.left = Math.min(yg.left, h.cx);
      }
    }

    // X callouts sit on a single baseline above the strip. Crowded labels slide right to
    // the next free slot and a jogged leader connects them back to their true position —
    // labels can never overlap regardless of density (CAD-style jogged ordinates). The
    // leader then runs down through every instance at that X, breaking at each punch.
    const baseY = this.strip.y - 26;
    const sortedX = [...xGroups.entries()].sort((a, b) => a[1].x - b[1].x);
    let lastLabelX = -Infinity;
    this.dimXs = sortedX.map(([label, g]) => {
      const labelX = Math.max(g.x, lastLabelX + 13);
      lastLabelX = labelX;
      const jog = `M ${labelX} ${baseY - 6} L ${g.x} ${baseY + 4}`;
      return {
        x: g.x,
        ext: `${jog} ${this.brokenVLine(g.x, baseY + 4, g.bottom)}`,
        labelX,
        labelY: baseY - 8,
        label,
      };
    });
    // Labels that slid right can overhang the drawing; widen the canvas so they stay visible.
    if (this.showDims && this.dimXs.length) {
      this.svgW = Math.max(this.svgW, lastLabelX + 40);
    }

    // Y callouts: a reference line from the row's left-most instance to the right edge of
    // the strip (where the label pins), broken at each punch; labels de-collide top-down
    // the same way the guide labels do.
    const sortedY = [...yGroups.entries()]
      .map(([label, g]) => ({
        y: g.y,
        labelY: g.y + 4,
        label,
        ext: this.brokenHLine(g.y, g.left, this.strip.x + this.strip.w),
      }))
      .sort((a, b) => a.y - b.y);
    let lastLabelY = -Infinity;
    for (const d of sortedY) {
      if (d.labelY - lastLabelY < 14) {
        d.labelY = lastLabelY + 14;
      }
      lastLabelY = d.labelY;
    }
    this.dimYs = sortedY;
  }

  // Reference lines pass THROUGH the punches they index, so they get a small drafting-style
  // break around every punch outline they cross (any punch, not just the row's own — a large
  // neighbor overlapping the line breaks it too).
  private brokenVLine(x: number, y1: number, y2: number): string {
    const cuts = this.holes
      .filter((h) => Math.abs(h.cx - x) <= h.halfW + 1)
      .map((h) => [h.cy - h.halfH - 3, h.cy + h.halfH + 3] as [number, number]);
    return this.brokenSegments(y1, y2, cuts)
      .map(([a, b]) => `M ${x} ${a} V ${b}`)
      .join(' ');
  }

  private brokenHLine(y: number, x1: number, x2: number): string {
    const cuts = this.holes
      .filter((h) => Math.abs(h.cy - y) <= h.halfH + 1)
      .map((h) => [h.cx - h.halfW - 3, h.cx + h.halfW + 3] as [number, number]);
    return this.brokenSegments(x1, x2, cuts)
      .map(([a, b]) => `M ${a} ${y} H ${b}`)
      .join(' ');
  }

  private brokenSegments(
    from: number,
    to: number,
    cuts: Array<[number, number]>
  ): Array<[number, number]> {
    const lo = Math.min(from, to);
    const hi = Math.max(from, to);
    const segs: Array<[number, number]> = [];
    let pos = lo;
    for (const [a, b] of cuts.sort((p, q) => p[0] - q[0])) {
      if (b <= pos || a >= hi) {
        continue;
      }
      if (a > pos) {
        segs.push([pos, a]);
      }
      pos = Math.max(pos, b);
      if (pos >= hi) {
        break;
      }
    }
    if (pos < hi) {
      segs.push([pos, hi]);
    }
    return segs;
  }

  // A horizontal dimension segment on the chain line below the strip. Short spans push the
  // label outside the arrows.
  private hDim(xa: number, xb: number, yPx: number, label: string, ext: string): RDimLine {
    const wide = Math.abs(xb - xa) >= 46;
    return {
      x1: xa,
      y1: yPx,
      x2: xb,
      y2: yPx,
      ext,
      label,
      labelX: wide ? (xa + xb) / 2 : Math.max(xa, xb) + 8,
      labelY: yPx - 5,
      anchor: wide ? 'middle' : 'start',
    };
  }

  private vDim(
    xPx: number,
    ya: number,
    yb: number,
    label: string,
    side: 'left' | 'right',
    ext: string
  ): RDimLine {
    return {
      x1: xPx,
      y1: ya,
      x2: xPx,
      y2: yb,
      ext,
      label,
      labelX: side === 'right' ? xPx + 6 : xPx - 6,
      labelY: (ya + yb) / 2 + 4,
      anchor: side === 'right' ? 'start' : 'end',
    };
  }

  // Hovering a hole measures it against every datum a shop drawing would: X as a chain
  // leading edge → hole → trailing edge (drawn below the strip, clear of the guide labels);
  // Y as a chain strip center → hole → nearest strip edge on the hole's right; and, for
  // flange-referenced punches, bend line (web flat) → hole on its left. The values match
  // what the info card reports.
  onHoleEnter(h: RHole): void {
    const v = this.view;
    if (!v) {
      return;
    }
    const dims: RDimLine[] = [];
    const bottom = this.strip.y + this.strip.h;
    const dimY = bottom + 26;
    const holeDownExt = `M ${h.cx} ${h.cy + h.arm + 3} V ${dimY + 5}`;

    if (Math.abs(h.cx - this.leadingEdgeX) >= 1) {
      dims.push(
        this.hDim(
          this.leadingEdgeX,
          h.cx,
          dimY,
          this.fmt(h.xIn),
          `M ${this.leadingEdgeX} ${bottom + 14} V ${dimY + 5} ${holeDownExt}`
        )
      );
    }
    if (Math.abs(h.cx - this.trailingEdgeX) >= 1) {
      dims.push(
        this.hDim(
          h.cx,
          this.trailingEdgeX,
          dimY,
          this.fmt(v.lengthIn - h.xIn),
          `M ${this.trailingEdgeX} ${bottom} V ${dimY + 5} ${holeDownExt}`
        )
      );
    }

    // Y chain on the hole's right: strip center → hole, then hole → nearest strip edge.
    const dx = h.cx + 18;
    const rightExt = `M ${h.cx + h.arm + 3} ${h.cy} H ${dx + 5}`;
    if (Math.abs(h.cy - this.midY) >= 1) {
      dims.push(
        this.vDim(
          dx,
          this.midY,
          h.cy,
          this.fmt(h.yIn),
          'right',
          `${rightExt} M ${h.cx} ${this.midY} H ${dx + 5}`
        )
      );
    }
    if (this.widthKnown && v.widthIn != null) {
      const edgeY = h.cy >= this.midY ? bottom : this.strip.y;
      const edgeIn = v.widthIn / 2 - Math.abs(h.yIn);
      if (edgeIn > 0.0005 && Math.abs(h.cy - edgeY) >= 1) {
        dims.push(this.vDim(dx, h.cy, edgeY, this.fmt(edgeIn), 'right', rightExt));
      }
    }

    // Flange-referenced: bend line → hole on the left side (the flange's own datum in the
    // flat), on whichever side of the web the hole resolved to.
    if (h.flangeRef) {
      const wf = h.yIn >= 0 ? this.webFlatPlusIn : this.webFlatMinusIn;
      if (wf != null && Math.abs(h.yIn) > Math.abs(wf)) {
        const wfY = this.plusFar ? this.midY - wf * this.scale : this.midY + wf * this.scale;
        const dxL = h.cx - 18;
        dims.push(
          this.vDim(
            dxL,
            wfY,
            h.cy,
            this.fmt(Math.abs(h.yIn) - Math.abs(wf)),
            'left',
            `M ${h.cx - h.arm - 3} ${h.cy} H ${dxL - 5}`
          )
        );
      }
    }

    this.hoverDims = dims;
    this.hoverInfo = h.tooltip.split('\n');
    const el = this.scrollerRef?.nativeElement;
    this.hoverInfoLeft = el ? h.cx - el.scrollLeft > el.clientWidth / 2 : false;
  }

  onHoleLeave(): void {
    this.hoverDims = [];
    this.hoverInfo = [];
  }
}
